import { useState, memo } from 'react';
import { ChevronDown, FileText, HardDrive } from 'lucide-react';
import { ChangelogRow } from '../../types/apogee';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../../utils/cn';
import { EmptyState } from '../ui/EmptyState';

interface ChangelogViewerProps {
  changelogs: ChangelogRow[];
  onLoadFull?: () => void;
  className?: string;
}

const ChangelogEntry = memo(({ entry }: { entry: ChangelogRow }) => {
  const [expanded, setExpanded] = useState(false);

  const htmlContent = expanded && entry.body_html_full
    ? entry.body_html_full
    : entry.body_html_excerpt;

  return (
    <div className="glass-card p-4 space-y-2">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 text-left"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-accent-300 shrink-0" />
          <span className="text-sm font-semibold text-white truncate tabular-nums">
            File #{entry.cf_file_id}
          </span>
          {entry.has_spilled_to_disk && (
            <span className="chip text-glass-500" title="Full changelog body is stored on disk">
              <HardDrive className="w-3 h-3" />
              disk
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-glass-400 tabular-nums">
            {formatDistanceToNow(new Date(entry.observed_at), { addSuffix: true })}
          </span>
          <ChevronDown className={cn('w-4 h-4 text-glass-400 transition-transform', expanded && 'rotate-180')} />
        </div>
      </button>

      {/* HTML content */}
      <div
        className={cn(
          'text-sm text-glass-300 prose prose-invert prose-sm max-w-none',
          'overflow-hidden',
          !expanded && 'max-h-32',
        )}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />

      {!expanded && htmlContent.length > 200 && (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs text-accent-300 hover:text-accent-200 transition-colors"
        >
          Show more
        </button>
      )}
    </div>
  );
});

ChangelogEntry.displayName = 'ChangelogEntry';

export const ChangelogViewer = memo(({ changelogs, className }: ChangelogViewerProps) => {
  if (changelogs.length === 0) {
    return (
      <EmptyState
        compact
        icon={FileText}
        title="No changelogs available"
        description="CurseForge changelogs appear here once enrichment has crawled this pack's files."
        className={className}
      />
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {changelogs.map((entry) => (
        <ChangelogEntry key={`${entry.cf_file_id}-${entry.observed_at}`} entry={entry} />
      ))}
    </div>
  );
});

ChangelogViewer.displayName = 'ChangelogViewer';
